import { useLayoutEffect, useRef, useState } from 'react';
import type { CSSProperties, ReactElement, ReactNode, RefObject } from 'react';
import { createPortal } from 'react-dom';
import type { CardInfoData } from './CardInfoCard.js';
import { CATALOG_CARD_HEIGHT, CATALOG_CARD_MIN_WIDTH, CatalogCard } from './CatalogCard.js';
import { Button, Modal } from './kit/index.js';

export type { CardInfoData, CardInfoStatus } from './CardInfoCard.js';

interface Position {
  left: number;
  top: number;
  width: number;
  scale: number;
  placement: 'above' | 'below';
}

const SUPPLEMENT_GAP = 8;
const MAX_VIEWPORT_FRACTION = 0.5;

export function CardInfoPopover({
  anchor,
  data,
  supplementData,
  testId,
  id,
  closing = false,
  onMouseEnter,
  onMouseLeave,
}: {
  anchor: HTMLElement | null;
  data: CardInfoData;
  /** Companion card (cell state) rendered to the right of the main card. */
  supplementData?: CardInfoData;
  testId: string;
  /** Stable id used by focusable triggers through aria-describedby. */
  id?: string;
  /** Whether the mounted popover is playing the shared exit animation. */
  closing?: boolean;
  /** Wire these to a hover bridge so the pointer can move onto (and click) the popover. */
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}): ReactElement | null {
  const [position, setPosition] = useState<Position | undefined>(undefined);
  const cardCount = supplementData === undefined ? 1 : 2;

  useLayoutEffect(() => {
    if (anchor === null) return;
    const update = (): void => {
      // Preserve the catalog-card footprint and scale it uniformly only when
      // the whole hover preview would cover more than half of either viewport
      // axis. Positioning uses the visual (scaled) bounds.
      const rect = anchor.getBoundingClientRect();
      const padding = 8;
      const gap = 8;
      const width = CATALOG_CARD_MIN_WIDTH * cardCount + SUPPLEMENT_GAP * (cardCount - 1);
      const maxVisualWidth = Math.max(
        1,
        Math.min(window.innerWidth - padding * 2, window.innerWidth * MAX_VIEWPORT_FRACTION),
      );
      const maxVisualHeight = Math.max(
        1,
        Math.min(window.innerHeight - padding * 2, window.innerHeight * MAX_VIEWPORT_FRACTION),
      );
      const scale = Math.min(1, maxVisualWidth / width, maxVisualHeight / CATALOG_CARD_HEIGHT);
      const visualWidth = width * scale;
      const visualHeight = CATALOG_CARD_HEIGHT * scale;
      const left = Math.max(padding, Math.min(rect.left + rect.width / 2 - visualWidth / 2, window.innerWidth - visualWidth - padding));
      const availableAbove = rect.top - gap - padding;
      const availableBelow = window.innerHeight - rect.bottom - gap - padding;
      const placement = availableAbove >= visualHeight || availableAbove >= availableBelow ? 'above' : 'below';
      const top = placement === 'above' ? rect.top - gap - visualHeight : rect.bottom + gap;
      setPosition({
        left,
        top: Math.max(padding, Math.min(top, window.innerHeight - visualHeight - padding)),
        width,
        scale,
        placement,
      });
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [anchor, data.defId, cardCount]);

  if (anchor === null || position === undefined || typeof document === 'undefined') return null;
  return createPortal(
    <div
      key={`${testId}:${data.defId}`}
      id={id}
      className="cards-card-hover-popover"
      data-testid={testId}
      data-placement={position.placement}
      data-closing={closing ? 'true' : undefined}
      role="tooltip"
      // React portal events still bubble through the logical card/cell tree.
      // Keep interactions such as expanding a keyword inside the preview from
      // triggering the anchor's selection, drag or editing controls.
      onPointerDownCapture={(event) => event.stopPropagation()}
      onClickCapture={(event) => event.stopPropagation()}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      // Measured anchor geometry is instance-owned; visual rules live in KIT_CSS.
      style={{
        left: position.left,
        top: position.top,
        width: position.width,
        '--cards-hover-popover-scale': position.scale,
      } as CSSProperties}
    >
      <CatalogCard data={data} testId={`${testId}-panel`} />
      {supplementData === undefined ? null : (
        <CatalogCard data={supplementData} testId={`${testId}-effects`} />
      )}
    </div>,
    document.body,
  );
}

export function CardInfoDialog({
  data,
  testId,
  onClose,
  supplement,
  actions,
  returnFocusRef,
}: {
  data: CardInfoData;
  testId: string;
  onClose: () => void;
  supplement?: ReactNode;
  actions?: ReactNode;
  returnFocusRef?: RefObject<HTMLElement | null>;
}): ReactElement | null {
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);

  if (typeof document === 'undefined') return null;
  const titleId = `${testId}-title`;
  const descriptionId = `${testId}-description`;
  return (
    <Modal
      portal
      layer="detail"
      overlayClassName="cards-card-detail-overlay"
      overlayTestId={`${testId}-overlay`}
      className={supplement === undefined ? 'cards-card-detail-dialog' : 'cards-card-detail-dialog cards-card-detail-dialog-wide'}
      labelledBy={titleId}
      describedBy={descriptionId}
      initialFocusRef={closeButtonRef}
      {...(returnFocusRef === undefined ? {} : { returnFocusRef })}
      dialogData={{ 'data-card-type': data.type }}
      onClose={onClose}
    >
      <div className="cards-card-detail-body">
        <CatalogCard
          data={data}
          testId={testId}
          titleId={titleId}
          descriptionId={descriptionId}
        />
        {supplement}
      </div>
      <Modal.Actions className={actions === undefined
        ? 'cards-card-detail-actions'
        : 'cards-card-detail-actions cards-card-detail-actions-inline'}>
        <Button
          variant="secondary"
          ref={closeButtonRef}
          className="cards-card-detail-close"
          data-testid={`${testId}-close`}
          onClick={onClose}
        >
          Закрыть
        </Button>
        {actions}
      </Modal.Actions>
    </Modal>
  );
}
